import React, {useEffect, useRef, useState, useContext} from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { NotificationsContext } from "@ui-components/Notifications";

import Button from "@ui-components/Button";

import useCanvasInit from "./hooks/useCanvasInit";
import useCanvasMode from "./hooks/useCanvasMode";

import { TaskMapColorDot } from "./TaskMapColorDot";
import { MODES, COLORS } from "./constants";
import Placeholder from "../../../ui-components/Placeholder";

function checkRequiredPin(canvasState) {
  return canvasState.objects.some((obj) => obj.name === "pin");
}

const TaskMapEditor = ({
    url,
    editMode,
    taskId = null,
    taskMapId,
    onSave,
    initialState=null,
    onClickElement = () => {},
    onLoad = (data) => {},
    zoomOnPin = false,
}) => {
  const { push } = useContext(NotificationsContext);
  const intl = useIntl();

  // refs
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // state
  const [mode, setMode] = useState("move");
  const [color, setColor] = useState("yellow");

  const [canvas, preSignedUrl, isCanvasLoading] = useCanvasInit(
    canvasRef,
    containerRef,
    url,
    taskId,
    taskMapId,
    initialState,
    zoomOnPin,
  );
  const [setUpDrawingMode, hasSelectedObject] = useCanvasMode(
    canvas,
    mode,
    preSignedUrl,
    color,
    setMode,
    taskId,
    taskMapId,
    onSave,
    onClickElement,
    (data) => {
      if (!checkRequiredPin(data)) {
        push({
          title: intl.formatMessage({ id: "task_map_editor_pin_required" }),
          type: "error",
        })
        return false;
      }
      return true;
    },
  );

  useEffect(() => {
    if (!canvas)
      return;

    onLoad(canvas.toDataURL({format: 'image/png'}, 1));
  }, [canvas, onLoad]);

  const controls = Object.entries(MODES)
    .filter(([_, m]) => editMode || !m.editModeOnly)
    .filter(([_, m]) => (m.visibleOn === "select" ? hasSelectedObject : true))
    .map(([key, m]) => (
      <Button
        key={key}
        full
        styleType={m.styleType ?? (key === mode ? "darkGreen" : "white")}
        className="cursor-pointer"
        borderCls="border shadow-sm border-am-700"
        textCls={m.textCls ?? "text-xs"}
        paddingCls="px-4 py-1"
        onClick={() => {
          setMode(key);
        }}
      >
        {m.icon}
        <FormattedMessage id={m.locale} />
      </Button>
    ));

  return (
    <div className="w-full h-full relative" ref={containerRef}>
      {/*---------- CONTROLS ----------*/}
      <div className="absolute top-3 right-3 flex flex-col gap-2 z-10 w-36">
        {mode === "highlight" && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {Object.entries(COLORS).map(([name, colorValue]) => (
              <TaskMapColorDot
                key={name}
                color={colorValue}
                selected={color === name}
                onClick={() => {
                  setColor(name);
                  const highlightColor = COLORS[name];
                  setUpDrawingMode(canvas, highlightColor);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="absolute top-3 left-3 flex flex-col gap-2 z-10 w-36">
        {controls}
      </div>
      {/*---------- CANVAS ----------*/}
      <div className="relative w-full h-full">
        <div className="border border-am-700" ref={canvasRef}>

        </div>
        {isCanvasLoading && (
          <div className="absolute z-10 top-0 left-0 w-full h-full bg-white">
            <Placeholder height="h-full" classNames="w-full" />
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskMapEditor;
